import {
    createEffect,
    on,
} from 'solid-js'

/**
 * TENANT-5814: close the auth modal the moment a session arrives from outside the page.
 *
 * The browser extension revokes the shared session on the API before its revoke broadcast reaches
 * an open tab, so an in-flight request 401s and the TENANT-5504 rejected-session path opens the
 * "session expired" modal. Logging back in through the extension then pushes a session into the
 * same tab (`useAuthSync` → `SessionService.checkAuthentication`): the header flips, but nothing
 * closed the modal, which kept asking the signed-in visitor to log in. The same applies to a
 * cross-tab login. A login that goes THROUGH the modal leaves the page for OAuth, so an open modal
 * while authentication flips true is always one of those external arrivals.
 *
 * Deferred: only a change in authentication closes the modal, never the mount. Two variants are
 * left alone: the non-dismissible one (`noClose`, where the modal is the whole page), and the
 * booking one, where a pending stay intent rides the modal's own OAuth hop — closing would drop
 * the intent and strand a now signed-in guest with no checkout and no re-prompt.
 *
 * @param {object} api
 * @param {() => boolean} api.isAuthenticated - `SessionService.isAuthenticated`.
 * @param {() => boolean} api.showAuthModal
 * @param {() => boolean} api.noClose
 * @param {() => (string | null)} api.bookingIntentToken
 * @param {() => void} api.closeAuthModal
 */
export const closeAuthModalWhenSessionArrives = ({
    bookingIntentToken,
    closeAuthModal,
    isAuthenticated,
    noClose,
    showAuthModal,
}) => {
    createEffect(on(isAuthenticated, (authenticated) => {
        if (!authenticated) return
        if (!showAuthModal() || noClose() || bookingIntentToken()) return
        closeAuthModal()
    }, {
        defer: true,
    }))
}
